import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, FolderOpen } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EventDetailSheet } from '@/components/calendar/EventDetailSheet';
import { QuickAddDialog } from '@/components/calendar/QuickAddDialog';
import { WeekView } from '@/components/calendar/WeekView';
import { MonthView } from '@/components/calendar/MonthView';
import { cn } from '@/lib/cn';
import type { CalendarEventDto } from '@cc/types';

type ViewMode = 'week' | 'month';

/**
 * Dedicated calendar surface. Week view default (the daily-driver), month view
 * for orientation. Account filter chips. Click event → detail sheet. Click empty
 * slot → quick-add prefilled with that time. Drag to reschedule on week view.
 */
export function CalendarPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [selected, setSelected] = useState<CalendarEventDto | null>(null);
  const [adding, setAdding] = useState(false);
  const [addPrefill, setAddPrefill] = useState<Date | undefined>(undefined);
  const [enabledAccounts, setEnabledAccounts] = useState<Set<string>>(new Set());
  // Project chips. Filter is "any of these projects, or unassigned if no chip
  // active". Empty set = show everything. Once Karl picks one or more, only
  // events linked to that/those projects (or unassigned, if he toggled
  // "unassigned" too) are visible.
  const [activeProjects, setActiveProjects] = useState<Set<string>>(new Set());
  const [includeUnassigned, setIncludeUnassigned] = useState(true);

  const { from, to } = useMemo(() => {
    if (viewMode === 'week') {
      const s = startOfWeek(cursor, { weekStartsOn: 0 });
      const e = endOfWeek(cursor, { weekStartsOn: 0 });
      return { from: s, to: addDays(e, 1) };
    }
    const s = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const e = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return { from: s, to: addDays(e, 1) };
  }, [viewMode, cursor]);

  const eventsQuery = useQuery({
    queryKey: qk.calendarEvents({ from: from.toISOString(), to: to.toISOString() }),
    queryFn: () =>
      api.listCalendarEvents({ from: from.toISOString(), to: to.toISOString() }),
    refetchInterval: 5 * 60_000,
  });
  const statusQuery = useQuery({
    queryKey: qk.calendarStatus,
    queryFn: () => api.listCalendarAccountStatuses(),
  });
  const allEvents = eventsQuery.data?.events ?? [];
  const accounts = (statusQuery.data?.accounts ?? []).filter((a) => a.hasCalendarScope);
  const accountColors = useMemo(() => makeAccountColors(accounts.map((a) => a.accountId)), [accounts]);

  // Only show project chips for projects that actually have events in view —
  // otherwise the row balloons to every project Karl has ever created.
  const projectsWithEvents = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of allEvents) {
      if (e.projectId && e.projectName) seen.set(e.projectId, e.projectName);
    }
    const list = Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [allEvents]);

  const projectFilterActive = activeProjects.size > 0;

  // Filter chips default to "all on" — toggle off via `enabledAccounts` set membership.
  // Empty set = all enabled; once user toggles one off it goes in the set as "disabled".
  const events = allEvents.filter((e) => {
    if (enabledAccounts.has(`disabled:${e.accountId}`)) return false;
    if (e.status === 'CANCELLED') return false;
    if (projectFilterActive) {
      // When any project chip is on, hide other linked projects.
      // Unassigned events follow the "Unassigned" toggle.
      if (e.projectId) return activeProjects.has(e.projectId);
      return includeUnassigned;
    }
    return true;
  });

  function navPrev() {
    setCursor(viewMode === 'week' ? subWeeks(cursor, 1) : subMonths(cursor, 1));
  }
  function navNext() {
    setCursor(viewMode === 'week' ? addWeeks(cursor, 1) : addMonths(cursor, 1));
  }
  function navToday() {
    setCursor(startOfDay(new Date()));
  }

  function handleSlotClick(d: Date) {
    setAddPrefill(d);
    setAdding(true);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground">
            {viewMode === 'week'
              ? `${format(from, 'MMM d')} – ${format(addDays(to, -1), 'MMM d, yyyy')}`
              : format(cursor, 'MMMM yyyy')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
            <Button
              variant={viewMode === 'week' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setViewMode('week')}
            >
              Week
            </Button>
            <Button
              variant={viewMode === 'month' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setViewMode('month')}
            >
              Month
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7" onClick={navPrev}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={navToday}>
              Today
            </Button>
            <Button variant="outline" size="sm" className="h-7" onClick={navNext}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Button
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              setAddPrefill(undefined);
              setAdding(true);
            }}
          >
            <Plus className="h-3 w-3" /> New
          </Button>
        </div>
      </div>

      {accounts.length === 0 && (
        <div className="rounded-md border border-border bg-muted/30 p-6 text-center">
          <CalendarIcon className="mx-auto h-6 w-6 text-muted-foreground" />
          <p className="mt-2 text-sm">No accounts have calendar access yet.</p>
          <Button asChild variant="outline" size="sm" className="mt-3">
            <a href="/settings/accounts">Connect calendar</a>
          </Button>
        </div>
      )}

      {accounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {accounts.map((a) => {
            const disabled = enabledAccounts.has(`disabled:${a.accountId}`);
            const color = accountColors[a.accountId];
            return (
              <button
                key={a.accountId}
                onClick={() => {
                  const next = new Set(enabledAccounts);
                  const key = `disabled:${a.accountId}`;
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  setEnabledAccounts(next);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                  disabled
                    ? 'border-border bg-transparent text-muted-foreground opacity-50'
                    : 'border-border bg-card text-foreground',
                )}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: color }}
                />
                {a.email}
              </button>
            );
          })}
        </div>
      )}

      {projectsWithEvents.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            <FolderOpen className="h-3 w-3" /> Project
          </span>
          {projectsWithEvents.map((p) => {
            const active = activeProjects.has(p.id);
            return (
              <button
                key={p.id}
                onClick={() => {
                  const next = new Set(activeProjects);
                  if (next.has(p.id)) next.delete(p.id);
                  else next.add(p.id);
                  setActiveProjects(next);
                }}
                className={cn(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                  active
                    ? 'border-primary bg-primary/15 text-foreground'
                    : 'border-border bg-card text-muted-foreground',
                )}
              >
                {p.name}
              </button>
            );
          })}
          {projectFilterActive && (
            <button
              onClick={() => setIncludeUnassigned((v) => !v)}
              className={cn(
                'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                includeUnassigned
                  ? 'border-border bg-card text-muted-foreground'
                  : 'border-border bg-transparent text-muted-foreground opacity-50',
              )}
              title="Toggle whether events with no linked project show when a filter is active"
            >
              Unassigned
            </button>
          )}
          {projectFilterActive && (
            <button
              onClick={() => {
                setActiveProjects(new Set());
                setIncludeUnassigned(true);
              }}
              className="text-[10px] text-muted-foreground hover:text-foreground"
            >
              clear
            </button>
          )}
        </div>
      )}

      {eventsQuery.isLoading ? (
        <Skeleton className="h-[600px]" />
      ) : viewMode === 'week' ? (
        <WeekView
          weekStart={from}
          events={events}
          accountColors={accountColors}
          onEventClick={setSelected}
          onSlotClick={handleSlotClick}
        />
      ) : (
        <MonthView
          monthStart={cursor}
          gridStart={from}
          events={events}
          accountColors={accountColors}
          onEventClick={setSelected}
          onDayClick={(d) => {
            // Clicking a month-view day jumps to that week with cursor on the day.
            setCursor(d);
            setViewMode('week');
          }}
        />
      )}

      <EventDetailSheet event={selected} onClose={() => setSelected(null)} />
      <QuickAddDialog
        open={adding}
        onOpenChange={setAdding}
        defaultStart={addPrefill}
        defaultDurationMin={30}
      />
    </div>
  );
}

const ACCOUNT_PALETTE = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#ef4444', // red
];

function makeAccountColors(ids: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  ids.forEach((id, i) => {
    out[id] = ACCOUNT_PALETTE[i % ACCOUNT_PALETTE.length]!;
  });
  return out;
}

